/**
 * Re-entry labels (`as` / `go`, PROCESS.md §7) — a per-process registry.
 *
 * A step named `as <label>` DEFINES a re-entry point; `go <label>` RE-ENTERS
 * it and resumes forward. Labels are process-LOCAL: the same name in two
 * processes is two distinct symbols. Neither has a workspace symbol id, so —
 * exactly like aspects (string label values) — we synthesize one keyed by the
 * owning process's identity plus the label name.
 *
 * This walk drives BOTH the SymbolTable (the label registry + cursor probe)
 * and the ReferenceIndex (each `go` is a recorded reference). Sharing one
 * traversal guarantees both sides compute the SAME `procKey`, so the ids line
 * up — the invariant the generic find-references / rename path relies on.
 *
 * Source of truth is the resolved model's `processes` / `subprocesses`: these
 * carry `ownerPath` (filled by the resolver, so the qualified identity is
 * known) AND retain per-file source spans (parser spans survive resolution).
 */

import type { Flow, SourceSpan } from "@archlang/engine";
import { forEachNode } from "@archlang/engine";
import type { Workspace } from "./workspace.js";

/** One occurrence of a re-entry label, with the file it lives in. */
export interface ReentryOccurrence {
  /** The owning process's qualified identity (`proc:Owner.Name` etc.). */
  readonly procKey: string;
  /** Bare label name (`checkpoint`). */
  readonly label: string;
  /** `as` = a definition site; `go` = a re-entry reference. */
  readonly role: "as" | "go";
  readonly uri: string;
  readonly span: SourceSpan;
}

/** Synthesized symbol id for a re-entry label. */
export function reentryLabelId(procKey: string, label: string): string {
  return `reentry:${procKey}:${label}`;
}

/**
 * The procKey for a process / subprocess. Mirrors the SymbolTable's qualified
 * names but with a discriminator so a process and a subprocess sharing a
 * qualified name keep separate label namespaces.
 */
export function procKeyOf(
  decl: { readonly ownerPath?: readonly string[]; readonly name: string },
  kind: "proc" | "subproc"
): string {
  const owner = decl.ownerPath && decl.ownerPath.length > 0
    ? decl.ownerPath.join(".")
    : undefined;
  const qname = owner ? `${owner}.${decl.name}` : decl.name;
  return `${kind}:${qname}`;
}

/**
 * Walk every process + subprocess in the workspace IR and emit every `as`
 * definition and `go` reference — via the shared `forEachNode` (one container
 * traversal, no bespoke re-walk). The span's `file` is the uri.
 */
export function forEachReentryOccurrence(
  workspace: Workspace,
  cb: (occ: ReentryOccurrence) => void
): void {
  const model = workspace.model();
  for (const pm of model.processModels ?? []) {
    if (pm.anonymous) continue;
    emitFlow(procKeyOf(pm, "proc"), pm.body, cb);
  }
  for (const sm of model.subprocessModels ?? []) {
    emitFlow(procKeyOf(sm, "subproc"), sm.body, cb);
  }
}

function emitFlow(procKey: string, body: Flow | undefined, cb: (occ: ReentryOccurrence) => void): void {
  const emitAs = (name: string, source: SourceSpan | undefined): void => {
    if (!source) return;
    cb({ procKey, label: name, role: "as", uri: source.file ?? "", span: source });
  };
  forEachNode(body, (n) => {
    if (n.kind === "call") {
      if (n.asName) emitAs(n.asName.name, n.asName.source);
    } else if (n.kind === "reentry") {
      const s = n.target.source;
      if (s) cb({ procKey, label: n.target.name, role: "go", uri: s.file ?? "", span: s });
    } else if (n.kind === "reversible") {
      // The `as` may sit on the reversible ITEM (forward-step `as` rides the
      // call node, handled above).
      for (const it of n.items) if (it.asName) emitAs(it.asName.name, it.asName.source);
    }
  });
}
